import { useEffect, useRef, useState } from 'react';
import { api } from '../../../shared/api.js';
import { Icon } from '../../../layout/Icon.jsx';
import { useRfidScan } from '../../../shared/useRfidScan.js';

export function CardScanInput({
  value,
  onChange,
  onVerified,
  autoFocus = true,
  /** يسمح باستعمال كارت عليه جلسات نشطة (كارت عائلة مشترك). */
  allowActive = false
}) {
  const inputRef = useRef(null);
  const [status, setStatus] = useState({ state: 'idle', message: '' });

  useRfidScan({
    inputRef,
    onScan: (uid) => {
      onChange?.(uid);
    },
    autoFocus,
    autoClear: false
  });

  useEffect(() => {
    const uid = (value || '').trim();
    if (uid.length < 4) {
      setStatus({ state: 'idle', message: '' });
      onVerified?.(null);
      return undefined;
    }
    let cancelled = false;
    setStatus({ state: 'checking', message: 'جاري التحقق...' });
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/cards/by-uid/${encodeURIComponent(uid)}`);
        if (cancelled) return;
        const card = data?.card || data?.data || data;
        if (!card) {
          setStatus({ state: 'invalid', message: 'كارت غير موجود' });
          onVerified?.(null);
          return;
        }
        const activeCount = Number(card.activeSessionsCount) || 0;
        if (card.status === 'available') {
          setStatus({ state: 'ok', message: 'الكارت متاح' });
          onVerified?.(card);
        } else if (allowActive && card.status === 'active') {
          // كارت عائلة: مسموح بإضافة طفل آخر عليه ما دام لنفس ولي الأمر.
          setStatus({
            state: 'ok',
            message: activeCount > 0 ? `كارت مشترك — ${activeCount} طفل عليه` : 'كارت مشترك'
          });
          onVerified?.(card);
        } else {
          const reason =
            card.status === 'active' ? 'الكارت مستخدم حالياً' :
            card.status === 'blocked' ? 'الكارت محظور' :
            card.status === 'lost' ? 'الكارت مفقود' :
            'حالة الكارت غير متاحة';
          setStatus({ state: 'invalid', message: reason });
          onVerified?.(null);
        }
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) {
          setStatus({ state: 'invalid', message: 'لم يُعثر على الكارت' });
        } else {
          setStatus({ state: 'invalid', message: 'تعذّر التحقق' });
        }
        onVerified?.(null);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const tone =
    status.state === 'ok' ? 'border-green text-green' :
    status.state === 'invalid' ? 'border-red text-red' :
    'border-line text-ink-3';

  return (
    <div>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input
            ref={inputRef}
            className="input pr-9"
            value={value || ''}
            placeholder="مرّر الكارت على القارئ أو اكتب UID"
            onChange={(e) => onChange?.(e.target.value)}
            dir="ltr"
            style={{ textAlign: 'right', fontFamily: 'monospace' }}
          />
          <Icon
            name="card"
            size={16}
            className="absolute top-1/2 -translate-y-1/2 right-3 text-ink-3 pointer-events-none"
          />
        </div>
        <div className={`pill border ${tone}`}>
          {status.state === 'ok' && <Icon name="check" size={12} />}
          {status.state === 'invalid' && <Icon name="x" size={12} />}
          <span className="text-[11px]">
            {status.state === 'ok' ? (status.message || 'متاح') :
              status.state === 'invalid' ? status.message :
              status.state === 'checking' ? '...' : 'بانتظار'}
          </span>
        </div>
      </div>
    </div>
  );
}
